import React, { useState, useEffect, useMemo } from 'react';
import { Search, Loader2, BookOpen, Tag } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { HelpService } from '@/services/helpService';
import type { HelpSearchResult, HelpCategory } from '@/types/help';
import { useDebounce } from '@/hooks/useDebounce';
import { logger } from '@/lib/logger';

interface HelpSearchProps {
  onArticleSelect?: (articleSlug: string) => void;
  placeholder?: string;
  className?: string;
}

export const HelpSearch: React.FC<HelpSearchProps> = ({
  onArticleSelect,
  placeholder = "Search help articles...",
  className = ""
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<HelpSearchResult[]>([]);
  const [categories, setCategories] = useState<HelpCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // Debounce search query to avoid too many API calls
  const debouncedQuery = useDebounce(query, 300);

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await HelpService.getCategories();
        setCategories(cats);
      } catch (error) {
        logger.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Perform search when debounced query changes
  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedQuery.trim()) {
        setResults([]);
        return;
      }

      setIsLoading(true);
      try {
        const searchResults = await HelpService.searchArticles(debouncedQuery, {
          category: selectedCategory || undefined,
        });
        setResults(searchResults);
      } catch (error) {
        logger.error('Error searching articles:', error);
        setResults([]);
        // You could show a toast or error message here if needed
      } finally {
        setIsLoading(false);
      }
    };

    performSearch();
  }, [debouncedQuery, selectedCategory]);

  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 px-0.5 rounded">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const categoryOptions = useMemo(() => {
    return categories.map(cat => ({
      value: cat.slug,
      label: cat.name,
    }));
  }, [categories]);

  return (
    <div className={`help-search ${className}`}>
      {/* Search Input */}
      <div className="relative mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            type="text"
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10 pr-4"
          />
          {isLoading && (
            <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Category Filter */}
      {categoryOptions.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedCategory === '' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory('')}
            >
              All Categories
            </Button>
            {categoryOptions.map(category => (
              <Button
                key={category.value}
                variant={selectedCategory === category.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(category.value)}
              >
                {category.label}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Search Results */}
      {query.trim() && (
        <div className="search-results">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span className="text-muted-foreground">Searching...</span>
            </div>
          )}

          {!isLoading && results.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <BookOpen className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No articles found for "{query}"</p>
              <p className="text-sm">Try different keywords or browse categories above</p>
            </div>
          )}

          {!isLoading && results.length > 0 && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground mb-4">
                Found {results.length} article{results.length !== 1 ? 's' : ''}
              </div>
              
              {results.map((result) => (
                <Card 
                  key={result.article.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => onArticleSelect?.(result.article.slug)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-2">
                          {highlightText(result.article.title, query)}
                        </h3>
                        
                        {result.article.excerpt && (
                          <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
                            {highlightText(result.article.excerpt, query)}
                          </p>
                        )}

                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Category Badge */}
                          <Badge variant="secondary" className="text-xs">
                            {categories.find(c => c.slug === result.article.category)?.name || result.article.category}
                          </Badge>

                          {/* Tags */}
                          {result.article.tags && result.article.tags.length > 0 && (
                            <>
                              {result.article.tags.slice(0, 3).map(tag => (
                                <Badge key={tag} variant="outline" className="text-xs">
                                  <Tag className="h-3 w-3 mr-1" />
                                  {tag}
                                </Badge>
                              ))}
                              {result.article.tags.length > 3 && (
                                <span className="text-xs text-muted-foreground">
                                  +{result.article.tags.length - 3} more
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HelpSearch;